import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Flame, LogOut, LayoutDashboard, Store, Tag, CreditCard, Shield, BarChart3, Menu, X, UtensilsCrossed } from "lucide-react";

const navItems = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/dashboard/business", label: "Business", icon: Store, testid: "nav-business" },
  { to: "/dashboard/offers", label: "Offers", icon: Tag, testid: "nav-offers" },
  { to: "/dashboard/orders", label: "Orders", icon: UtensilsCrossed, testid: "nav-orders" },
  { to: "/dashboard/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/dashboard/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header className="sticky top-0 z-50 glass border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="navbar-logo">
          <span className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
            <Flame className="w-4 h-4 text-white" />
          </span>
          <span className="font-display text-lg font-bold tracking-tight">DealWisp</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => {
            const active = location.pathname === item.to;
            return (
              <Link key={item.to} to={item.to} data-testid={item.testid}>
                <span className={`px-3 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${active ? "bg-secondary text-white" : "text-muted-foreground hover:text-white"}`}>
                  <item.icon className="w-4 h-4" /> {item.label}
                </span>
              </Link>
            );
          })}
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin">
              <span className="px-3 py-2 rounded-md text-sm flex items-center gap-2 text-primary hover:text-white transition-colors">
                <Shield className="w-4 h-4" /> Admin
              </span>
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <span className="hidden sm:block text-sm text-muted-foreground" data-testid="navbar-user">{user?.name}</span>
          <Button variant="ghost" size="sm" className="md:hidden text-muted-foreground" onClick={() => setMenuOpen((o) => !o)} data-testid="mobile-menu-btn">
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn" className="text-muted-foreground hover:text-white">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {menuOpen && (
        <nav className="md:hidden border-t border-white/10 px-5 py-3 flex flex-col gap-1" data-testid="mobile-nav">
          {[...navItems, ...(user?.role === "admin" ? [{ to: "/admin", label: "Admin", icon: Shield, testid: "m-nav-admin" }] : [])].map((item) => (
            <Link key={item.to} to={item.to} onClick={() => setMenuOpen(false)} className={`px-3 py-2.5 rounded-md text-sm flex items-center gap-3 ${location.pathname === item.to ? "bg-secondary text-white" : "text-muted-foreground"}`}>
              <item.icon className="w-4 h-4" /> {item.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}
